import { isEmpty } from 'bold-ui'
import { formatDecimalOrInteger } from 'bold-ui/lib/util/number'
import { isNil, max } from 'lodash'

import { GridArea } from '../cell/classes/GridArea'
import { PivotTableCellType } from '../cell/model'
import { PivotTableCellProps } from '../cell/PivotTableCell'
import { KeyMap } from '../model'
import { PivotTableTreeNode } from './model'

interface VerticalTableProps<T extends object> {
  cellData: PivotTableCellModel<T>[]
  keys: Array<keyof T>
  tree: PivotTableTreeNode<T>
  keysMapping: KeyMap<T>
  rowHeaderSpace?: number
  mixedTable?: {
    totalKey?: keyof T
  }
  showTotals: boolean
}

interface VerticalTableResults {
  divs: PivotTableCellProps[]
  rowTotalValues: Map<string, number>
  totalRowNumber: number
  cellPositions: string[]
}

interface HorizontalTableProps<T extends object> {
  cellData: PivotTableCellModel<T>[]
  keys: Array<keyof T>
  tree: PivotTableTreeNode<T>
  keysMapping: KeyMap<T>
  columnHeaderSpace?: number
  mixedTable?: MixedTableProps<T>
  showTotals: boolean
}

interface MixedTableProps<T extends object> {
  rowResult: PivotTableCellModel<T>[]
  rowTotalValues: Map<string, number>
  totalKey: keyof T
  totalRowNumber: number
  cellPositions: string[]
}

interface CellInitialPosition {
  parentInitialPosition?: CellInitialPosition
  auxInitialPosition?: CellInitialPosition
  cellSpan?: SpanValue
}

interface PivotTableCellModel<T> {
  cellSpan: SpanValue
  cellValue: string | number
  initialPosition: CellInitialPosition
  path: string
  column?: number
  row?: number
  key: keyof T
  total?: number
}

interface SpanValue {
  value: number
}

interface StackObj {
  treeNode: any
  spanList?: SpanValue[]
  parentIni?: CellInitialPosition
  path?: string
  column?: number
  row?: number
}

interface PivotTableBuildOptions {
  /**
   * Controls whether total rows and columns should be rendered in the pivot table
   */
  showTotals: boolean
}

const RESULT_PATH_KEY = 'RESULT'
const PATH_SEPARATOR = '|'
const TOTAL = 'Total'
const EMPTY = '-'
const IGNORED_TREE_KEYS = ['id', '__typename', 'nodeKey', 'nodeValue', 'maxLeafValue']

export function buildTable<T extends object>(
  rowKeys: (keyof T)[],
  columnKeys: (keyof T)[],
  defaultTree: PivotTableTreeNode<T>,
  complementaryTree: PivotTableTreeNode<T>,
  keysMapping: KeyMap<T>,
  buildOptions: PivotTableBuildOptions
): PivotTableCellProps[] {
  const { showTotals } = buildOptions

  const tableCells = getBaseTableCells(rowKeys, columnKeys, defaultTree, complementaryTree, keysMapping, showTotals)

  if (!showTotals && !isEmpty(tableCells)) {
    let maxRow = 0
    let maxCol = 0

    for (const cell of tableCells) {
      if (cell.gridArea.rowEnd > maxRow) maxRow = cell.gridArea.rowEnd
      if (cell.gridArea.columnEnd > maxCol) maxCol = cell.gridArea.columnEnd
    }

    return tableCells.map((cells) => ({
      ...cells,
      isEndRow: cells.isEndRow || cells.gridArea.rowEnd === maxRow,
      isEndColumn: cells.isEndColumn || cells.gridArea.columnEnd === maxCol,
    }))
  }

  return tableCells
}

function getBaseTableCells<T extends object>(
  rowKeys: (keyof T)[],
  columnKeys: (keyof T)[],
  defaultTree: PivotTableTreeNode<T>,
  complementaryTree: PivotTableTreeNode<T>,
  keysMapping: KeyMap<T>,
  showTotals: boolean
): PivotTableCellProps[] {
  if (rowKeys?.length && complementaryTree?.nodeValue !== undefined && columnKeys?.length) {
    return buildMixedTable<T>(defaultTree, keysMapping, rowKeys, columnKeys, complementaryTree, showTotals)
  }

  if (rowKeys?.length) {
    return buildVerticalTable<T>(defaultTree, keysMapping, rowKeys, showTotals)
  }

  if (columnKeys?.length) {
    return buildHorizontalTable<T>(defaultTree, keysMapping, columnKeys, showTotals)
  }

  return []
}

function buildHorizontalTable<T extends object>(
  defaultTree: PivotTableTreeNode<T>,
  keysMapping: KeyMap<T>,
  columnKeys: (keyof T)[],
  showTotals: boolean
): PivotTableCellProps[] {
  const cellData = getListOfCellDataFromTree(defaultTree, 'row', keysMapping)
  const divs = getHorizontal<T>({
    cellData,
    keys: columnKeys,
    tree: defaultTree,
    keysMapping,
    showTotals,
  })

  return divs
}

function buildVerticalTable<T extends object>(
  defaultTree: PivotTableTreeNode<T>,
  keysMapping: KeyMap<T>,
  rowKeys: (keyof T)[],
  showTotals: boolean
): PivotTableCellProps[] {
  const rowResult = getListOfCellDataFromTree(defaultTree, 'column', keysMapping)
  const { divs } = getVertical<T>({
    cellData: rowResult,
    keys: rowKeys,
    tree: defaultTree,
    keysMapping,
    rowHeaderSpace: 2,
    showTotals,
  })

  return divs
}

function buildMixedTable<T extends object>(
  defaultTree: PivotTableTreeNode<T>,
  keysMapping: KeyMap<T>,
  rowKeys: (keyof T)[],
  columnKeys: (keyof T)[],
  complementaryTree: PivotTableTreeNode<T>,
  showTotals: boolean
): PivotTableCellProps[] {
  const rowResult = getListOfCellDataFromTree(defaultTree, 'column', keysMapping, rowKeys)
  const { divs: horizontalDivs, rowTotalValues, totalRowNumber, cellPositions: cellPosition } = getVertical({
    cellData: rowResult,
    keys: rowKeys,
    tree: defaultTree,
    keysMapping,
    rowHeaderSpace: columnKeys.length + 1,
    mixedTable: {
      totalKey: columnKeys[0],
    },
    showTotals,
  })

  const columnResult = getListOfCellDataFromTree(complementaryTree, 'row', keysMapping, columnKeys)
  const verticalDivs = getHorizontal<T>({
    cellData: columnResult,
    keys: columnKeys,
    tree: complementaryTree,
    keysMapping,
    columnHeaderSpace: rowKeys.length + 1,
    mixedTable: {
      rowResult: rowResult,
      rowTotalValues: rowTotalValues,
      totalKey: rowKeys[0],
      totalRowNumber: totalRowNumber,
      cellPositions: cellPosition,
    },
    showTotals,
  })

  return horizontalDivs.concat(verticalDivs)
}

function getVertical<T extends object>(params: VerticalTableProps<T>): VerticalTableResults {
  const { cellData, keys, tree, keysMapping, rowHeaderSpace = 1, mixedTable, showTotals } = params

  let divs: PivotTableCellProps[] = []
  const rowTotalValues = new Map<string, number>()
  let cellPositions: string[] = []
  const isMixedTable = !isNil(mixedTable)

  const tableHeaderDivs = buildVerticalTableHeader<T>(keys, rowHeaderSpace, keysMapping, showTotals, isMixedTable)
  divs = divs.concat(tableHeaderDivs)

  const valuesAndKeys = buildVerticalValuesAndKeys<T>(
    cellData,
    isMixedTable,
    mixedTable,
    rowTotalValues,
    keys,
    rowHeaderSpace
  )
  divs = divs.concat(valuesAndKeys.divs)
  cellPositions = cellPositions.concat(valuesAndKeys.cellPositions)

  if (showTotals) {
    const tableTotals = buildVerticalTableTotals<T>(
      isMixedTable,
      valuesAndKeys.maxRowEnd,
      keys,
      rowHeaderSpace,
      valuesAndKeys.maxColumnEnd,
      tree.nodeValue
    )
    divs = divs.concat(tableTotals.divs)
    cellPositions = cellPositions.concat(tableTotals.cellPositions)
  }

  return { divs, rowTotalValues, totalRowNumber: valuesAndKeys.maxRowEnd, cellPositions }
}

function buildVerticalValuesAndKeys<T extends object>(
  cellData: PivotTableCellModel<T>[],
  isMixedTable: boolean,
  mixedTable: { totalKey?: keyof T },
  rowTotalValues: Map<string, number>,
  keys: (keyof T)[],
  rowHeaderSpace: number
): { maxRowEnd: number; maxColumnEnd: number; divs: PivotTableCellProps[]; cellPositions: string[] } {
  const divs: PivotTableCellProps[] = []
  const cellPositions: string[] = []
  let maxRowEnd = 0
  let maxColumnEnd = 0
  for (const cell of cellData) {
    if (isMixedTable) {
      if (cell.key === mixedTable.totalKey) {
        const rowTotalValuesKey: string = cell.path.replace(
          getCurrentPath(cell.key.toString(), cell.cellValue.toString()),
          ''
        )
        rowTotalValues.set(rowTotalValuesKey, cell.total || 0)
      }
      if (!keys.includes(cell.key)) {
        continue
      }
    }
    const lastKey = mixedTable && cell.key === keys[keys.length - 1]
    const { cellValue: value, cellSpan: span, column: columnStart = 0 } = cell
    const columnEnd = lastKey ? columnStart + 2 : columnStart + 1
    const rowStart = getCellInitialPosition(cell.initialPosition) + rowHeaderSpace
    const rowEnd = rowStart + span.value
    maxRowEnd = max([rowEnd, maxRowEnd])
    maxColumnEnd = columnEnd > maxColumnEnd ? columnStart : maxColumnEnd

    const valuesGridArea = new GridArea(rowStart, columnStart, rowEnd, columnEnd)
    const formattedValue = typeof value === 'number' ? formatDecimalOrInteger(value) : value

    divs.push({
      gridArea: valuesGridArea,
      types: new Set(cell.key !== RESULT_PATH_KEY ? [PivotTableCellType.HEADER] : [PivotTableCellType.VALUE]),
      children: formattedValue,
      isEndColumn: cell.key === RESULT_PATH_KEY && !mixedTable,
    })

    cellPositions.push(valuesGridArea.toString())
  }
  return { maxRowEnd, maxColumnEnd, divs, cellPositions }
}

function getHorizontal<T extends object>(params: HorizontalTableProps<T>): PivotTableCellProps[] {
  const { cellData, keys, tree, keysMapping, columnHeaderSpace = 1, mixedTable, showTotals } = params
  let divs: PivotTableCellProps[] = []
  let cellPositions = mixedTable?.cellPositions || []
  const mixedTableStartRowCache = new Map<string, number>()
  const mixedTableColumnTotals = new Map<number, number>()

  const isMixedTable = !isNil(mixedTable)

  const tableHeaderDivs = buildHorizontalTableHeader<T>(keys, columnHeaderSpace, keysMapping, showTotals, isMixedTable)
  divs = divs.concat(tableHeaderDivs)

  const valuesAndKeys = buildHorizontalValuesAndKeys<T>(
    cellData,
    columnHeaderSpace,
    mixedTable,
    mixedTableColumnTotals,
    keys,
    mixedTableStartRowCache
  )
  divs = divs.concat(valuesAndKeys.divs)
  cellPositions = cellPositions.concat(valuesAndKeys.cellPositions)

  if (mixedTable) {
    const tableTotals = buildMixedTableTotals<T>(
      valuesAndKeys.maxColumnEnd,
      keys,
      mixedTable.rowTotalValues,
      mixedTableColumnTotals,
      mixedTable.totalRowNumber,
      mixedTableStartRowCache,
      columnHeaderSpace,
      tree.nodeValue,
      cellPositions,
      showTotals
    )
    divs = divs.concat(tableTotals.divs)
  } else if (showTotals) {
    const tableTotalsDivs = buildHorizontalTableTotals(
      valuesAndKeys.maxRowEnd,
      valuesAndKeys.maxColumnEnd,
      tree.nodeValue
    )
    divs = divs.concat(tableTotalsDivs)
  }

  return divs
}

function buildHorizontalValuesAndKeys<T extends object>(
  cellData: PivotTableCellModel<T>[],
  columnHeaderSpace: number,
  mixedTable: MixedTableProps<T>,
  mixedTableColumnTotals: Map<number, number>,
  keys: (keyof T)[],
  mixedTableStartRowCache: Map<string, number>
): { maxRowEnd: number; maxColumnEnd: number; divs: PivotTableCellProps[]; cellPositions: string[] } {
  const divs: PivotTableCellProps[] = []
  const cellPositions: string[] = []
  let maxRowEnd = 0
  let maxColumnEnd = 0
  for (const cell of cellData) {
    const value = cell.cellValue
    const columnSpan = cell.cellSpan
    let rowStart = cell.row || 0
    let columnStart = getCellInitialPosition(cell.initialPosition) + columnHeaderSpace
    if (mixedTable) {
      if (cell.key === mixedTable.totalKey) {
        mixedTableColumnTotals.set(columnStart, cell.total || 0)
      }
      if (!keys.includes(cell.key) && cell.key !== RESULT_PATH_KEY) {
        continue
      }
      if (mixedTable.rowResult && cell.key === RESULT_PATH_KEY) {
        const rows = mixedTable.rowResult.filter((rx) => cell.path.indexOf(rx.path) !== -1)
        rowStart = getCellInitialPosition(rows[rows.length - 1].initialPosition) + keys.length + 1
        columnStart = getCellInitialPosition(cell.initialPosition.parentInitialPosition) + columnHeaderSpace
        mixedTableStartRowCache.set(rows[rows.length - 1].path, rowStart)
      }
    }
    const lastKey = mixedTable && cell.key === keys[keys.length - 1]
    const rowEnd = lastKey ? rowStart + 2 : rowStart + 1
    const columnEnd = columnStart + columnSpan.value
    maxRowEnd = max([rowEnd, maxRowEnd])
    maxColumnEnd = columnEnd > maxColumnEnd ? columnStart : maxColumnEnd
    const gridArea = new GridArea(rowStart, columnStart, rowEnd, columnEnd)

    if (cell.key === RESULT_PATH_KEY) {
      const formattedValue = typeof value === 'number' ? formatDecimalOrInteger(value) : value
      divs.push({
        types: new Set([PivotTableCellType.VALUE]),
        gridArea: gridArea,
        children: formattedValue,
        isEndRow: mixedTable === undefined,
      })
    } else {
      divs.push({
        types: new Set([PivotTableCellType.HEADER]),
        gridArea: gridArea,
        children: value,
      })
    }
    cellPositions.push(gridArea.toString())
  }
  return { maxRowEnd, maxColumnEnd, divs, cellPositions }
}

function buildVerticalTableHeader<T extends object>(
  keys: (keyof T)[],
  rowHeaderSpace: number,
  keysMapping: KeyMap<T>,
  showTotals: boolean,
  isMixedTable: boolean
): PivotTableCellProps[] {
  const divs: PivotTableCellProps[] = []
  keys.forEach((k, i) => {
    const colEnd = getHeaderSpanEnd(i, keys.length, showTotals, isMixedTable)

    const headerGridArea = new GridArea(rowHeaderSpace, i + 1, rowHeaderSpace + 1, colEnd)
    divs.push({
      types: new Set([PivotTableCellType.HEADER]),
      gridArea: headerGridArea,
      children: keysMapping.get(k).keyName,
    })
  })
  return divs
}

function buildHorizontalTableHeader<T extends object>(
  keys: (keyof T)[],
  columnHeaderSpace: number,
  keysMapping: KeyMap<T>,
  showTotals: boolean,
  isMixedTable: boolean
): PivotTableCellProps[] {
  const divs: PivotTableCellProps[] = []
  keys.forEach((k, i) => {
    const rowEnd = getHeaderSpanEnd(i, keys.length, showTotals, isMixedTable)

    const headerGridArea = new GridArea(i + 1, columnHeaderSpace, rowEnd, columnHeaderSpace + 1)
    divs.push({
      types: new Set([PivotTableCellType.HEADER]),
      gridArea: headerGridArea,
      children: keysMapping.get(k).keyName,
    })
  })
  return divs
}

function buildMixedTableTotals<T extends object>(
  maxColumnEnd: number,
  keys: (keyof T)[],
  mixedTableRowTotals: Map<string, number>,
  mixedTableColumnTotals: Map<number, number>,
  totalRowNumber: number,
  mixedTableStartRowCache: Map<string, number>,
  columnHeaderSpace: number,
  grandtotal: number,
  cellPositions: string[],
  showTotals: boolean
): { divs: PivotTableCellProps[] } {
  let divs: PivotTableCellProps[] = []

  const intersectionGridArea = new GridArea(keys.length + 1, columnHeaderSpace, keys.length + 2, columnHeaderSpace + 1)
  divs.push({
    types: new Set([PivotTableCellType.HEADER]),
    gridArea: intersectionGridArea,
  })

  let updatedPositions = cellPositions

  if (showTotals) {
    const totalsGridArea = new GridArea(1, maxColumnEnd + 1, keys.length + 2, maxColumnEnd + 2)
    const dataValueGridArea = new GridArea(totalRowNumber, maxColumnEnd + 1, totalRowNumber + 1, maxColumnEnd + 2)

    const columnTotals = buildMixedTableColumnTotals(totalRowNumber, mixedTableColumnTotals)
    const rowTotals = buildMixedTableRowTotals(mixedTableStartRowCache, maxColumnEnd, mixedTableRowTotals)
    divs = divs.concat(columnTotals.divs, rowTotals.divs)
    updatedPositions = [
      ...updatedPositions,
      ...columnTotals.cellPositions,
      ...rowTotals.cellPositions,
      dataValueGridArea.toString(),
    ]

    divs.push({
      types: new Set([PivotTableCellType.HEADER]),
      isEndColumn: true,
      gridArea: totalsGridArea,
      children: TOTAL,
    })
    divs.push({
      types: new Set([PivotTableCellType.TOTAL, PivotTableCellType.VALUE, PivotTableCellType.GRANDTOTAL]),
      isEndColumn: true,
      isEndRow: true,
      gridArea: dataValueGridArea,
      children: grandtotal,
    })
  }

  const endColLimit = showTotals ? maxColumnEnd + 2 : maxColumnEnd + 1
  const endRowLimit = showTotals ? totalRowNumber + 1 : totalRowNumber

  for (let column = columnHeaderSpace + 1; column < endColLimit; column++) {
    for (let row = keys.length + 2; row < endRowLimit; row++) {
      const gridArea = new GridArea(row, column, row + 1, column + 1)
      if (!updatedPositions.includes(gridArea.toString())) {
        divs.push({
          types: new Set([PivotTableCellType.EMPTY]),
          isEndColumn: column === endColLimit - 1,
          isEndRow: row === endRowLimit - 1,
          gridArea: gridArea,
          children: EMPTY,
        })
      }
    }
  }
  return { divs }
}

function buildHorizontalTableTotals(
  maxRowEnd: number,
  maxColumnEnd: number,
  grandTotal: number
): PivotTableCellProps[] {
  const divs: PivotTableCellProps[] = []
  const totalsGridArea = new GridArea(maxRowEnd - 1, 1, maxRowEnd, 2)
  const dataValueGridArea = new GridArea(maxRowEnd - 1, maxColumnEnd + 1, maxRowEnd, maxColumnEnd + 2)
  const totalGridArea = new GridArea(1, maxColumnEnd + 1, maxRowEnd - 1, maxColumnEnd + 2)
  divs.push({
    types: new Set([PivotTableCellType.HEADER]),
    isEndColumn: true,
    gridArea: totalGridArea,
    children: TOTAL,
  })
  divs.push({
    types: new Set([PivotTableCellType.HEADER]),
    isEndRow: true,
    gridArea: totalsGridArea,
    children: TOTAL,
  })
  divs.push({
    types: new Set([PivotTableCellType.TOTAL, PivotTableCellType.VALUE, PivotTableCellType.GRANDTOTAL]),
    isEndRow: true,
    isEndColumn: true,
    gridArea: dataValueGridArea,
    children: grandTotal,
  })
  return divs
}

function buildMixedTableColumnTotals(
  totalRowNumber: number,
  mixedTableColumnTotals: Map<number, number>
): { divs: PivotTableCellProps[]; cellPositions: string[] } {
  const divs: PivotTableCellProps[] = []
  const cellPositions: string[] = []
  mixedTableColumnTotals.forEach((value, key) => {
    const gridArea = new GridArea(totalRowNumber, key, totalRowNumber + 1, key + 1)
    divs.push({
      types: new Set([PivotTableCellType.TOTAL, PivotTableCellType.VALUE]),
      isEndRow: true,
      gridArea: gridArea,
      children: value,
    })
    cellPositions.push(gridArea.toString())
  })
  return { divs, cellPositions }
}

function buildMixedTableRowTotals(
  mixedTableStartRowCache: Map<string, number>,
  maxColumnEnd: number,
  mixedTableRowTotals: Map<string, number>
): { divs: PivotTableCellProps[]; cellPositions: string[] } {
  const divs: PivotTableCellProps[] = []
  const cellPositions: string[] = []
  mixedTableRowTotals.forEach((value, key) => {
    const rowNumber = mixedTableStartRowCache.get(key)
    if (rowNumber) {
      const gridArea = new GridArea(rowNumber, maxColumnEnd + 1, rowNumber + 1, maxColumnEnd + 2)
      divs.push({
        types: new Set([PivotTableCellType.TOTAL, PivotTableCellType.VALUE]),
        isEndColumn: true,
        gridArea: gridArea,
        children: value,
      })
      cellPositions.push(gridArea.toString())
    }
  })
  return { divs, cellPositions }
}

function buildVerticalTableTotals<T extends object>(
  isMixedTable: boolean,
  maxRowEnd: number,
  keys: (keyof T)[],
  rowHeaderSpace: number,
  maxColumnEnd: number,
  grandtotal: number
): { divs: PivotTableCellProps[]; cellPositions: string[] } {
  const divs: PivotTableCellProps[] = []
  const cellPositions: string[] = []
  let totalsGridArea: GridArea
  if (isMixedTable) {
    totalsGridArea = new GridArea(maxRowEnd, 1, maxRowEnd + 1, keys.length + 2)
  } else {
    totalsGridArea = new GridArea(rowHeaderSpace, maxColumnEnd, rowHeaderSpace + 1, maxColumnEnd + 1)
    const totalGridArea = new GridArea(maxRowEnd, 1, maxRowEnd + 1, maxColumnEnd)
    const dataValueGridArea = new GridArea(maxRowEnd, maxColumnEnd, maxRowEnd + 1, maxColumnEnd + 1)
    divs.push({
      types: new Set([PivotTableCellType.HEADER]),
      isEndRow: true,
      gridArea: totalGridArea,
      children: TOTAL,
    })
    divs.push({
      types: new Set([PivotTableCellType.TOTAL, PivotTableCellType.GRANDTOTAL, PivotTableCellType.VALUE]),
      isEndRow: true,
      isEndColumn: true,
      gridArea: dataValueGridArea,
      children: grandtotal,
    })
  }
  divs.push({
    types: new Set([PivotTableCellType.HEADER]),
    isEndRow: isMixedTable,
    isEndColumn: !isMixedTable,
    gridArea: totalsGridArea,
    children: TOTAL,
  })
  cellPositions.push(totalsGridArea.toString())

  return { divs, cellPositions }
}

function getHeaderSpanEnd(index: number, totalLength: number, showTotals: boolean, isMixedTable: boolean) {
  const DEFAULT_HEADER_SPAN_OFFSET = 2
  const EXPANDED_HEADER_SPAN_OFFSET = 3

  const isLastKey = index === totalLength - 1
  const shouldExpandLastHeader = isLastKey && !showTotals && !isMixedTable

  return shouldExpandLastHeader ? index + EXPANDED_HEADER_SPAN_OFFSET : index + DEFAULT_HEADER_SPAN_OFFSET
}

function getCurrentPath(filterKey: string, value: string): string {
  return PATH_SEPARATOR + filterKey + '.' + value
}

/**
 * Sums the span values of the parent cells to find the starting coordinates of the current cell.
 */
function getCellInitialPosition(initialPositionTree: CellInitialPosition | undefined) {
  const stack: CellInitialPosition[] = []
  let sum = 1

  if (initialPositionTree) {
    stack.push(initialPositionTree)
  } else {
    return 0
  }

  // Loop through the stack to calculate the sum of cellSpans
  while (stack.length) {
    const cell = stack.pop()
    if (cell.cellSpan) {
      sum += cell.cellSpan.value
    }
    // Add children to the stack
    if (cell.auxInitialPosition) {
      stack.push(cell.auxInitialPosition)
    } else if (cell.parentInitialPosition) {
      stack.push(cell.parentInitialPosition)
    }
  }

  return sum
}

function getListOfCellDataFromTree<T extends object>(
  tree: PivotTableTreeNode<T>,
  increment: 'column' | 'row',
  keyMapping: KeyMap<T>,
  onlyIncreaseSpanOnKeys?: Array<keyof T>
): PivotTableCellModel<T>[] {
  const stack: StackObj[] = []
  const cellList: PivotTableCellModel<T>[] = []

  // Initialize the stack with the first node of the tree
  stack.push({ treeNode: tree, [increment]: 1 })

  // loop through the stack
  while (stack.length) {
    const obj = stack.shift()
    if (obj) {
      let lastCellSpan: SpanValue
      let lastInitialValue: CellInitialPosition
      const currentNodeKey = obj.treeNode.nodeKey
      const shouldIncreaseCellSpan = !onlyIncreaseSpanOnKeys || onlyIncreaseSpanOnKeys.includes(currentNodeKey)

      // loop through children nodes
      Object.keys(obj.treeNode)
        .filter((k) => !IGNORED_TREE_KEYS.includes(k))
        .sort(keyMapping.get(currentNodeKey)?.ordenator)
        .forEach((key) => {
          // cellSpan: size of a cell
          const cellSpan = { value: 1 }
          // cellSpanList: list of current + previous cellSpans. To be used on the next loop cycle
          const cellSpanList = [cellSpan]
          if (obj.spanList) {
            const myKeys = Object.keys(obj.treeNode).filter((k) => !IGNORED_TREE_KEYS.includes(k))
            const lastSpan = obj.spanList[0]
            if (myKeys.length > lastSpan.value && shouldIncreaseCellSpan) {
              for (let i = 0; i < obj.spanList.length; i++) {
                /**
                 * This increment might cause side effects on all existing SpanValue objects,
                 * including the ones inside CellInitialPosition objects. It is intentional
                 */
                obj.spanList[i].value++
              }
            }
            cellSpanList.push(...obj.spanList)
          }

          const rowOrColumn = obj[increment] || 0
          const parentIni = obj.parentIni
          const value = keyMapping.get(currentNodeKey)?.formatter?.(key) ?? key
          const currentPath: string = getCurrentPath(currentNodeKey, value)
          const path = obj.path ? obj.path + currentPath : currentPath

          const initialPosition: CellInitialPosition = {
            parentInitialPosition: parentIni,
            cellSpan: shouldIncreaseCellSpan ? lastCellSpan : { value: 0 },
            auxInitialPosition: lastInitialValue,
          }

          // Push a key cell to the result list
          cellList.push({
            cellSpan: cellSpan,
            cellValue: value,
            initialPosition,
            [increment]: rowOrColumn,
            path,
            key: obj.treeNode.nodeKey,
            total: obj.treeNode.nodeValue,
          })

          if (obj.treeNode[key].nodeKey === undefined) {
            // If it's a leaf node, push a value cell to the result list
            cellList.push({
              cellSpan: cellSpan,
              [increment]: rowOrColumn + 1,
              initialPosition: initialPosition,
              path: path + PATH_SEPARATOR + RESULT_PATH_KEY,
              cellValue: obj.treeNode[key].nodeValue,
              key: RESULT_PATH_KEY as keyof T,
            })
          } else {
            // If it isn't a leaf node, add itself to the stack
            stack.push({
              treeNode: obj.treeNode[key],
              spanList: cellSpanList,
              [increment]: rowOrColumn + 1,
              parentIni: initialPosition,
              path,
            })
          }
          lastInitialValue = initialPosition
          lastCellSpan = cellSpan
        })
    }
  }
  return cellList
}
